// Remembers which institution login page (`/<endpoint>`) and which tab
// (teacher / student) this device last signed in through, so logging out —
// or an expired session — returns the user to their school's page with the
// right tab already selected, instead of the admin-only MyDeskey portal.
//
// Stored under its own key, which sign-out deliberately keeps.

import { DEVICE_ID_KEY, endServerSession } from './deviceSession';
import { queryClient } from './queryClient';

const STORAGE_KEY = 'deskey_login_endpoint';
const ENDPOINT_ROLES = ['teacher', 'student'];
// Same shape the backend accepts for an endpoint (slug_utils.validate_custom_slug).
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PORTAL_PATH = '/mydeskey';

/** @returns {{ slug: string, role: 'teacher'|'student' } | null} */
export function getRememberedLoginEndpoint() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && SLUG_PATTERN.test(saved.slug) && ENDPOINT_ROLES.includes(saved.role)) {
      return { slug: saved.slug, role: saved.role };
    }
  } catch { /* unreadable storage — behave as if nothing was saved */ }
  return null;
}

/** Save the endpoint + tab a teacher or student just signed in through. */
export function rememberLoginEndpoint(slug, role) {
  if (!SLUG_PATTERN.test(slug || '') || !ENDPOINT_ROLES.includes(role)) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ slug, role }));
  } catch { /* storage blocked — the user just lands on the portal next time */ }
}

/** Drop the saved endpoint, e.g. when the school renamed it and it no longer exists. */
export function forgetLoginEndpoint(slug) {
  const saved = getRememberedLoginEndpoint();
  if (!saved || (slug && saved.slug !== slug)) return;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* nothing to clean up */ }
}

/** Where a signed-out teacher or student should be sent. */
export function signedOutPath() {
  const saved = getRememberedLoginEndpoint();
  return saved ? `/${saved.slug}` : PORTAL_PATH;
}

/** Where a user lands right after signing in, by role. */
export function postLoginPath(user) {
  const role = user?.role;
  if (role === 'tenant_owner' || role === 'tenant_admin') return '/institution';
  if (role === 'department_admin' || role === 'department_head') return '/department';
  if (role === 'teacher') return user.licence_status === 'active' ? '/teacher' : '/activate-licence';
  if (role === 'student') {
    // Legacy users have the flag at the top level, newer ones in student_profile.
    const onboarded = user.student_profile?.onboarding_completed || user.onboarding_completed;
    return onboarded ? '/student/desk' : '/student/onboarding';
  }
  return '/';
}

/**
 * Sign-out: `localStorage.clear()` that keeps the remembered login endpoint
 * and this browser's device id (so signing in again reuses its device slot).
 * Also tells the server to free the slot, unless it already signed us out.
 */
export function clearStorageKeepLoginEndpoint({ notifyServer = true } = {}) {
  if (notifyServer) endServerSession();
  const saved = getRememberedLoginEndpoint();
  let deviceId = null;
  try { deviceId = localStorage.getItem(DEVICE_ID_KEY); localStorage.clear(); } catch { return; }
  if (saved) rememberLoginEndpoint(saved.slug, saved.role);
  if (deviceId) localStorage.setItem(DEVICE_ID_KEY, deviceId);
  // Drop every saved page so the next person on this browser starts clean.
  queryClient.clear();
}
